import React, { useCallback, useRef } from "react";
import { Text } from "@chakra-ui/react";
import { FiUpload, FiX, FiFile } from "react-icons/fi";
import * as Yup from "yup";
import { formatFileSize } from "../utils/format-file-size";

interface RevisionFilePickerProps {
	error?: string;
	files: File[];
	onChange: (files: File[]) => void;
}

const ALLOWED_MIME_TYPES = [
	"application/pdf",
	"application/msword",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
	"application/vnd.ms-excel",
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	"application/zip",
	"image/jpeg",
	"image/png",
	"text/csv",
];

const MAX_FILE_SIZE = 50 * 1024 * 1024;
const MAX_FILE_COUNT = 20;

export const revisionFileSchema = Yup.array()
	.of(
		Yup.mixed<File>()
			.test("fileType", "File type not allowed", (value) => {
				if (!value) {
					return true;
				}
				return ALLOWED_MIME_TYPES.includes((value as File).type);
			})
			.test("fileSize", "File must be 50MB or less", (value) => {
				if (!value) {
					return true;
				}
				return (value as File).size <= MAX_FILE_SIZE;
			})
	)
	.max(MAX_FILE_COUNT, `Maximum ${MAX_FILE_COUNT} files allowed`)
	.min(1, "At least one file is required");

export const RevisionFilePicker: React.FC<RevisionFilePickerProps> = ({
	files,
	onChange,
	error,
}) => {
	const inputRef = useRef<HTMLInputElement>(null);

	const handleFileChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const selectedFiles = e.target.files;
			if (!selectedFiles) {
				return;
			}
			const newFiles = [...files, ...Array.from(selectedFiles)].slice(
				0,
				MAX_FILE_COUNT
			);
			onChange(newFiles);
			if (inputRef.current) {
				inputRef.current.value = "";
			}
		},
		[files, onChange]
	);

	const handleRemove = useCallback(
		(index: number) => {
			const updated = files.filter((_, i) => i !== index);
			onChange(updated);
		},
		[files, onChange]
	);

	const handleDrop = useCallback(
		(e: React.DragEvent<HTMLDivElement>) => {
			e.preventDefault();
			e.stopPropagation();
			const droppedFiles = e.dataTransfer.files;
			if (!droppedFiles) {
				return;
			}
			const newFiles = [...files, ...Array.from(droppedFiles)].slice(
				0,
				MAX_FILE_COUNT
			);
			onChange(newFiles);
		},
		[files, onChange]
	);

	const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
	}, []);

	return (
		<div data-testid="revision-file-picker">
			<div
				className="cursor-pointer rounded-lg border-2 border-dashed border-gray-300 p-4 text-center transition-colors hover:border-blue-400 hover:bg-blue-50"
				onClick={() => inputRef.current?.click()}
				onDrop={handleDrop}
				onDragOver={handleDragOver}
				role="button"
				tabIndex={0}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						inputRef.current?.click();
					}
				}}
				data-testid="file-drop-zone"
			>
				<FiUpload className="mx-auto mb-2 h-6 w-6 text-gray-400" />
				<Text className="text-sm text-gray-600">
					Click to upload or drag and drop
				</Text>
				<Text className="text-xs text-gray-400">
					PDF, Word, Excel, ZIP, Images, CSV. Max 50MB per file, up to 20 files.
				</Text>
			</div>
			<input
				ref={inputRef}
				type="file"
				multiple
				className="hidden"
				onChange={handleFileChange}
				accept={ALLOWED_MIME_TYPES.join(",")}
				data-testid="file-input"
			/>
			{error && (
				<Text className="mt-1 text-xs text-red-500" data-testid="file-error">
					{error}
				</Text>
			)}
			{files.length > 0 && (
				<div className="mt-3 space-y-1" data-testid="file-list">
					{files.map((file, index) => (
						<div
							key={`${file.name}-${index}`}
							className="flex items-center gap-2 rounded-md bg-gray-50 px-3 py-2"
							data-testid={`staged-file-${index}`}
						>
							<FiFile className="h-4 w-4 flex-shrink-0 text-gray-400" />
							<span className="flex-1 truncate text-sm text-gray-700">
								{file.name}
							</span>
							<span className="flex-shrink-0 text-xs text-gray-400">
								{formatFileSize(file.size)}
							</span>
							<button
								type="button"
								onClick={(e) => {
									e.stopPropagation();
									handleRemove(index);
								}}
								className="flex-shrink-0 rounded p-0.5 text-gray-400 hover:bg-red-50 hover:text-red-500"
								data-testid={`remove-file-${index}`}
							>
								<FiX className="h-4 w-4" />
							</button>
						</div>
					))}
					<Text className="text-xs text-gray-400">
						{files.length} / {MAX_FILE_COUNT} files selected
					</Text>
				</div>
			)}
		</div>
	);
};
